<template>

  <div>

    <b-row>
      <b-col class="text-left">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          class="my-1"
          @click="showFilters = !showFilters"
        >
          <FeatherIcon icon="FilterIcon" />
          Filters
        </b-button>
      </b-col>
      <b-col class="text-right">
        <b-button
          variant="primary"
          class="my-1"
          @click="$router.push({ name: 'add-warehouses' })"
        >
          Add warehouse
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Container Card -->
    <b-card
      v-if="showFilters"
      no-body
      class="mb-2"
    >
      <b-form
        class="m-2"
        @keyup.enter="fetchList"
      >
        <!-- Filters -->
        <b-row>
          <b-col
            lg="12"
            md="12"
          >
            <b-row>
              <!-- Name filter -->
              <b-col>
                <b-form-group
                  label="Name"
                  label-for="name"
                >
                  <b-form-input
                    id="name"
                    v-model="filters.name"
                    name="name"
                    autocomplete="off"
                  />
                </b-form-group>
              </b-col>
              <!-- Country filter -->
              <b-col>
                <b-form-group
                  label="Country"
                  label-for="country"
                >
                  <b-form-input
                    id="country"
                    v-model="filters.country"
                    name="country"
                    autocomplete="off"
                  />
                </b-form-group>
              </b-col>
              <b-col class="text-right">
                <b-button
                  class="mt-2"
                  variant="primary"
                  @click="fetchList"
                >
                  <span class="text-nowrap">Filter</span>
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Content -->
    <b-card
      no-body
    >

      <b-card-actions
        ref="loadingCard"
        no-actions
      >
        <b-table
          ref="refUserListTable"
          class="position-relative"
          :items="dataList"
          :fields="tableColumns"
          :busy="loading"
          stacked="sm"
          primary-key="id"
          show-empty
          empty-text="No matching records found"
        >

          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>Loading...</strong>
            </div>
          </template>

          <template
            v-if="userCompaniesCount > 1"
            #cell(logo)="data"
          >
            <CompanyThumb
              :logo="data.item.company.logo"
              :name="data.item.company.name"
            />
          </template>

          <!-- Column: Created at -->
          <template #cell(created_at)="data">
            {{ data.item.created_at | formatDate }}
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-col>
              <b-button-group>
                <b-button
                  v-if="$permissions().hasPermission('edit warehouse')"
                  variant="primary"
                  size="sm"
                  @click="edit(data.item.id)"
                >
                  <feather-icon
                    icon="EditIcon"
                    class="d-inline"
                  />
                </b-button>
                <b-button
                  v-if="$permissions().hasPermission('delete warehouse')"
                  v-b-tooltip.hover.top="'Delete warehouse'"
                  variant="outline-danger"
                  size="sm"
                  @click="deleteItem(data.item.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="d-inline"
                  />
                </b-button>
              </b-button-group>
            </b-col>
          </template>

        </b-table>
      </b-card-actions>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, VBTooltip, BFormInput, BFormGroup, BButtonGroup, BForm, BSpinner,
} from 'bootstrap-vue'
import handleError from '@/views/components/errorHandler'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Vue from 'vue'
import { searchQuery } from '@core/utils/filter'
import Ripple from 'vue-ripple-directive'
import { EventBus } from '@core/utils/utils'
import CompanyThumb from '@/views/components/CompanyThumb.vue'

export default {
  components: {
    CompanyThumb,
    BSpinner,
    BForm,
    BCard,
    BCardActions,
    BRow,
    BCol,
    BButton,
    BTable,
    BFormInput,
    BFormGroup,
    BButtonGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      loading: false,
      showFilters: true,
      filters: {
        name: null,
        country: null,
      },
      dataList: [],
    }
  },
  computed: {
    userCompaniesCount() {
      return Vue.prototype.$profile().data.user.companies.length
    },
    hasAccessToAll() {
      return this.$permissions().hasPermission('view all warehouses')
    },
    tableColumns() {
      const result = []
      if (this.userCompaniesCount > 1) {
        result.push({ key: 'logo', label: 'Company', sortable: false, class: 'image-column' })
      }
      result.push({ key: 'name', label: 'Name', sortable: true })
      result.push({
        key: 'code',
        label: 'Code',
        sortable: true,
        thClass: 'd-none d-sm-table-cell',
        tdClass: 'd-none d-sm-table-cell',
      })
      result.push({ key: 'country', label: 'Country', sortable: true })
      if (this.hasAccessToAll) {
        result.push({ key: 'company.name', label: 'Company', sortable: true })
      }
      result.push({ key: 'created_at', sortable: true, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' })
      if (
        this.$permissions().hasPermission('edit warehouse')
        || this.$permissions().hasPermission('delete warehouse')
      ) {
        result.push({ key: 'actions' })
      }
      return result
    },
  },
  mounted() {
    EventBus.$on('companyListUpdated', this.fetchList)
    this.fetchList()
  },
  beforeDestroy() {
    EventBus.$off('companyListUpdated', this.fetchList)
  },
  methods: {
    edit(id) {
      this.$router.push({ name: 'edit-warehouses', params: { warehouseId: id } })
    },
    deleteItem(id) {
      this.$http.delete(`/v1/companies/${this.$activeCompany().data.company.id}/warehouses/${id}`)
        .then(() => {
          this.$router.push({ name: 'warehouses' })
          this.fetchList()
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    fetchList() {
      this.loading = true
      this.$http.get(searchQuery(`/v1/companies/${this.$activeCompany().data.company.id}/warehouses`, this.filters))
        .then(response => {
          this.$data.dataList = response.data.data
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
